import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Tabs as BasedTabs } from '@ets/design-system';

import { useSelector } from '_common/hooks';
import { useGetMetadataListQuery } from 'App/redux/MetadataApi';

import styles from './Tabs.module.scss';
import Collaborators from '../Collaborators/Collaborators';
import Metadata from '_common/components/Metadata/Metadata';
import GroupPanel from '../Panels/GroupPanel';
import ObjectPanel from '../Panels/ObjectPanel';
import SpacePanel from '../Panels/SpacePanel';

export type TabType = 'general' | 'metadata';

type TabsProps = {
  selectedTab: TabType;
  onChange: (newTab: TabType) => void;
};

const Tabs = ({ selectedTab, onChange }: TabsProps) => {
  const elementId = Object.keys(useSelector((state) => state.table.selected))[0];
  const object = useSelector((state) => state.app.data[elementId]);

  const { data: metadata } = useGetMetadataListQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: result.data ?? {} }),
  });

  const hasMetadata = object?.type === 'document' && Object.keys(metadata).length > 0;

  useEffect(() => {
    if (!hasMetadata) {
      onChange('general');
    }
  }, [elementId]);

  const handleClickTab = (id: TabType) => {
    onChange(id);
  };

  const renderBodyByType = () => {
    switch (object.type) {
      case 'file':
      case 'document':
      case 'folder':
      case 'dopdf':
      case 'presentation':
        return <ObjectPanel object={object} />;

      case 'space':
        return <SpacePanel object={object as Space} />;

      case 'group':
        return <GroupPanel object={object as Group} />;

      default:
        return null;
    }
  };

  return (
    <>
      <BasedTabs
        value={selectedTab}
        onValueChange={(value) => handleClickTab(value as TabType)}
        className={styles.root}
      >
        <BasedTabs.List flex>
          <BasedTabs.Trigger value="general">
            <FormattedMessage id="settings.tabs.general" />
          </BasedTabs.Trigger>
          {hasMetadata && (
            <BasedTabs.Trigger value="metadata">
              <FormattedMessage id="METADATA" />
            </BasedTabs.Trigger>
          )}
        </BasedTabs.List>
        <div className={styles.body}>
          <BasedTabs.Content value="general">
            <>
              <Collaborators />
              {renderBodyByType()}
            </>
          </BasedTabs.Content>
          {hasMetadata && (
            <BasedTabs.Content value="metadata">
              <Metadata id={elementId} />
            </BasedTabs.Content>
          )}
        </div>
      </BasedTabs>
    </>
  );
};

export default Tabs;
